import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Download, Heart } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", data: null });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", data: null }); return; }
    let active = true;
    const start = Date.now();
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
        if (!active) return;
        if (data.status === "completed") { setState({ status: "completed", data }); return; }
        if (data.status === "failed") { setState({ status: "failed", data }); return; }
      } catch {
        if (!active) return;
      }
      if (Date.now() - start > 60000) { setState({ status: "timeout", data: null }); return; }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  const { status, data } = state;

  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="payment-success-page">
      {status === "checking" ? (
        <div className="flex flex-col items-center gap-3 py-12 text-muted-foreground" data-testid="payment-checking">
          <Loader2 className="animate-spin" size={30} />
          <p className="text-sm">Confirmation du paiement en cours…</p>
        </div>
      ) : status === "completed" ? (
        <div data-testid="payment-completed">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-600 flex items-center justify-center mb-4">
            <CheckCircle2 className="text-white" size={36} />
          </div>
          <h1 className="font-heading font-bold text-2xl text-emerald-700 dark:text-emerald-400">Soutien confirmé !</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Merci pour votre soutien de <b>${data?.amount}</b> à <b>{data?.media_name}</b>. Votre paiement par carte a bien été reçu.
          </p>
          <p className="text-[11px] text-muted-foreground mt-1 font-mono">Réf : {data?.reference}</p>
          <a href={`${API}/supports/${data?.reference}/receipt`} target="_blank" rel="noopener noreferrer"
             data-testid="payment-receipt-btn"
             className="mt-5 inline-flex items-center justify-center gap-2 w-full h-11 rounded-md border border-border bg-secondary text-secondary-foreground text-sm font-semibold hover:bg-border transition-colors">
            <Download size={16} /> Télécharger le reçu (PDF)
          </a>
          <Link to="/" className="mt-3 inline-flex items-center justify-center gap-2 w-full h-11 rounded-md bg-primary text-white text-sm font-semibold hover:bg-rdcblue transition-colors">
            Retour à l'accueil
          </Link>
        </div>
      ) : (
        <div data-testid="payment-failed">
          <div className="mx-auto w-16 h-16 rounded-full bg-rdcred flex items-center justify-center mb-4">
            <XCircle className="text-white" size={36} />
          </div>
          <h1 className="font-heading font-bold text-2xl text-rdcred">Paiement non confirmé</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {status === "timeout"
              ? "Le paiement prend plus de temps que prévu. S'il a été débité, votre soutien sera confirmé sous peu."
              : "Le paiement n'a pas pu être confirmé. Aucun montant n'a été prélevé si la transaction a échoué."}
          </p>
          <Link to="/" className="mt-5 inline-flex items-center justify-center gap-2 w-full h-11 rounded-md bg-secondary text-sm font-semibold hover:bg-border transition-colors">
            Retour à l'accueil
          </Link>
        </div>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="payment-cancel-page">
      <div className="mx-auto w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
        <Heart className="text-muted-foreground" size={32} />
      </div>
      <h1 className="font-heading font-bold text-2xl">Paiement annulé</h1>
      <p className="text-sm text-muted-foreground mt-2">Vous avez annulé le paiement. Aucun montant n'a été prélevé. Vous pouvez réessayer à tout moment.</p>
      <Link to="/" className="mt-5 inline-flex items-center justify-center gap-2 w-full h-11 rounded-md bg-primary text-white text-sm font-semibold hover:bg-rdcblue transition-colors">
        Retour à l'accueil
      </Link>
    </div>
  );
}
